/* @flow */

import { computed, when } from 'mobx';

import { ApiModel } from 'api/ApiModel';
import type { WidgetItem } from 'stores/DashboardStore';
import { widgetPreferenceToWidgetItem } from 'stores/UserStore';
import { userStore } from 'services/ServiceRegistry';
import { OptimisticObservable } from 'utils/mobx';

export default class WidgetStore extends ApiModel {
  @computed get defaultWidgets(): WidgetItem[] {
    return [
      {
        position: 0,
        variant: 'default',
        widget: 'AssetsByUserType',
      },
      {
        position: 1,
        variant: 'default',
        widget: 'Contracts',
      },
      {
        position: 2,
        variant: 'default',
        widget: 'AssetStatus',
      },
      {
        position: 3,
        variant: 'default',
        widget: 'Calibrations',
      },
    ];
  }

  @computed get widgets(): WidgetItem[] {
    return this._optimisticWidgets.data.sort((first, second) => {
      if (first.position < second.position) return -1;
      if (first.position > second.position) return 1;
      return 0;
    });
  }

  @computed get favouriteWidgets(): string[] {
    return this._optimisticFavouriteWidgets.data;
  }

  set widgets(newWidgets: WidgetItem[]) {
    this._optimisticWidgets.data = newWidgets;
  }

  set favouriteWidgets(newWidgets: string[]) {
    this._optimisticFavouriteWidgets.data = newWidgets;
  }

  updateFavouriteWidgets = async (newFavouriteWidgets: string[]) => {
    await when(() => userStore.activeUser);

    if (userStore.isReadOnly) {
      return;
    }

    const updateIds = {
      favouriteWidgets: this._optimisticFavouriteWidgets.markRequested(),
      widgets: this._optimisticWidgets.markRequested(),
    };

    const actions: { action: 'delete' | 'add', name: string }[] = [];

    actions.push(
      ...userStore.activeUser.favourite_widgets
        .filter(favouriteWidget => !newFavouriteWidgets.includes(favouriteWidget))
        .map(favouriteToDelete => ({
          action: 'delete',
          name: favouriteToDelete,
        }))
    );

    actions.push(
      ...newFavouriteWidgets
        .filter(
          newFavouriteWidget => !userStore.activeUser.favourite_widgets.includes(newFavouriteWidget)
        )
        .map(favouriteToAdd => ({
          action: 'add',
          name: favouriteToAdd,
        }))
    );

    await Promise.all(
      actions.map(action => {
        switch (action.action) {
          case 'add':
            return this.api.createResource('user/widgets/favourites', {
              name: action.name,
            });

          case 'delete':
            return this.api.deleteResource(`user/widgets/favourites/${action.name}`);
        }
      })
    );

    await userStore.getActiveUser(updateIds);
  };

  updateWidgets = async (newWidgets: WidgetItem[]) => {
    await when(() => userStore.activeUser);

    if (userStore.isReadOnly) {
      return;
    }

    const updateIds = {
      favouriteWidgets: this._optimisticFavouriteWidgets.markRequested(),
      widgets: this._optimisticWidgets.markRequested(),
    };

    const actions: {
      action: 'delete' | 'add',
      widgetItem: WidgetItem | (WidgetItem & { id: string }),
    }[] = [];
    const mappedWidgetPreferences = userStore.activeUser.widget_preferences.map(
      widgetPreferenceToWidgetItem(true)
    );

    actions.push(
      ...mappedWidgetPreferences
        .filter(
          widgetPreference =>
            !newWidgets.some(
              newWidget =>
                newWidget.widget === widgetPreference.widget &&
                newWidget.position === widgetPreference.position
            )
        )
        .map(widget => ({ action: 'delete', widgetItem: widget }))
    );

    actions.push(
      ...newWidgets
        .filter(
          newWidget =>
            !mappedWidgetPreferences.some(
              widgetPreference =>
                newWidget.widget === widgetPreference.widget &&
                newWidget.position === widgetPreference.position
            )
        )
        .map(widget => ({ action: 'add', widgetItem: widget }))
    );

    await Promise.all(
      actions.map(action => {
        switch (action.action) {
          case 'add':
            return this.api.createResource('user/widgets/preferences', {
              name: action.widgetItem.widget,
              position: action.widgetItem.position,
              variant: action.widgetItem.variant,
            });

          case 'delete':
            return this.api.deleteResource(`user/widgets/preferences/${action.widgetItem.id}`);
        }
      })
    );

    await userStore.getActiveUser(updateIds);
  };

  _optimisticWidgets: OptimisticObservable<WidgetItem[]> = new OptimisticObservable(
    this.defaultWidgets,
    this.updateWidgets
  );
  _optimisticFavouriteWidgets: OptimisticObservable<string[]> = new OptimisticObservable(
    [],
    this.updateFavouriteWidgets
  );
}
